import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Alert } from './shared';

const CATEGORIES = ['analysis_report', 'technical_snapshot', 'event_brief', 'ops_playbook'];

export default function AdminInstrumentIngest() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [canonicalId, setCanonicalId] = useState(searchParams.get('canonical_id') || '');
    const [category, setCategory] = useState('analysis_report');
    const [content, setContent] = useState('');
    const [contentType, setContentType] = useState('markdown');
    const [structuredText, setStructuredText] = useState('{}');
    const [attributesText, setAttributesText] = useState('{"labels": []}');
    const [ttlSeconds, setTtlSeconds] = useState('');
    const [targets, setTargets] = useState({ s3: true, rag: true });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const handleSubmit = async () => {
        if (!canonicalId || !content) {
            setError('canonical_id and content are required');
            return;
        }
        let structured = null;
        let attributes = {};
        try {
            structured = structuredText.trim() ? JSON.parse(structuredText) : null;
            attributes = attributesText.trim() ? JSON.parse(attributesText) : {};
        } catch (e) {
            setError('Invalid JSON in structured or attributes');
            return;
        }
        const selectedTargets = Object.entries(targets).filter(([, v]) => v).map(([k]) => k);

        try {
            setSaving(true);
            const payload = {
                canonical_id: canonicalId,
                category,
                content,
                content_type: contentType,
                structured,
                attributes,
                ttl_seconds: ttlSeconds ? Number(ttlSeconds) : null,
                targets: selectedTargets,
            };
            const res = await adminApi.ingestInstrumentInsight(payload);
            setResult(res);
            setError(null);
        } catch (err) {
            setError(err.message || 'Ingest failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Button variant="ghost" size="xs" onClick={() => navigate('/admin/instrument-intelligence')}>← Back</Button>
                    <h1 className="text-lg font-semibold text-gray-900 mt-1">Manual Ingest Insight</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Post a new versioned insight — publishes to DB + outbox + S3/RAG sinks.
                    </p>
                </div>
                <Button size="sm" disabled={saving} onClick={handleSubmit}>
                    {saving ? 'Publishing...' : 'Publish'}
                </Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {result && (
                <Alert variant="success" onClose={() => setResult(null)} className="mb-4">
                    Published <code>{result.document_id}</code> (insight_id: {result.insight_id})
                </Alert>
            )}

            <div className="space-y-4 max-w-4xl">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Canonical ID</label>
                    <input
                        type="text"
                        value={canonicalId}
                        onChange={(e) => setCanonicalId(e.target.value)}
                        placeholder="equity:HOSE:VNM"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                    />
                </div>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Content Type</label>
                        <select value={contentType} onChange={(e) => setContentType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                            <option value="markdown">markdown</option>
                            <option value="json">json</option>
                            <option value="html">html</option>
                            <option value="plain">plain</option>
                        </select>
                    </div>
                    <div className="w-40">
                        <label className="block text-xs font-medium text-gray-600 mb-1">TTL (seconds)</label>
                        <input
                            type="number"
                            value={ttlSeconds}
                            onChange={(e) => setTtlSeconds(e.target.value)}
                            placeholder="auto"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Content</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={12}
                        placeholder="# VNM Q1/2026 analysis..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Structured (JSON)</label>
                        <textarea
                            value={structuredText}
                            onChange={(e) => setStructuredText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Attributes (JSON)</label>
                        <textarea
                            value={attributesText}
                            onChange={(e) => setAttributesText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Sink Targets</label>
                    <div className="flex items-center gap-4">
                        {['s3', 'rag'].map((s) => (
                            <label key={s} className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={targets[s]}
                                    onChange={(e) => setTargets({ ...targets, [s]: e.target.checked })}
                                />
                                {s.toUpperCase()}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
